import { useEffect, useRef, useState } from "react";

/**
 * Flips to true the first time the element comes within `rootMargin` of the
 * viewport, and stays true. Lets a costly child (a PDF page render, a billed
 * Mapbox map load) wait until the visitor actually scrolls to it.
 *
 * Without IntersectionObserver (old browsers, jsdom) it reports visible at
 * once, so content never stays hidden.
 */
export function useHasBeenVisible<T extends Element>(rootMargin = "200px") {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        setVisible(true);
      },
      { rootMargin }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, visible] as const;
}
